import {
  Background,
  type Edge,
  PanOnScrollMode,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useMy, useUploadFile } from '@/hooks';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { CustomBezierEdge } from '@/sections/attacks/attackPaths/components/edges';
import { nodeTypes } from '@/sections/attacks/attackPaths/config';
import {
  markerColors,
  techniqueColors,
} from '@/sections/attacks/attackPaths/constants';
import {
  AttackContext,
  type AttackContextType,
} from '@/sections/attacks/attackPaths/context';
import type { CustomNode } from '@/sections/attacks/attackPaths/types';
import {
  getAttackPathIdFromName,
  getAttackPathNameFromId,
} from '@/sections/attacks/attackPaths/utils';
import { computeLayout } from '@/sections/attacks/attackPaths/utils/computeLayout';

const edgeTypes = { bezier: CustomBezierEdge };

// Module-level constant so a null-edges graph coalesces to a STABLE identity —
// a fresh [] per render would defeat memoization keyed on the edges reference.
const EMPTY_EDGES: Array<{ id: string; source: string; target: string }> = [];

const noop = () => {};

// Minimal stub context for read-only rendering — nodes only read isEditMode and removeNode
const readOnlyContext: AttackContextType = {
  attackPaths: [],
  name: '',
  nodes: [],
  edges: [],
  stages: [],
  hosts: [],
  engagement: {},
  isEditMode: false,
  selectedAttackPath: '',
  activeView: 'graph',
  layoutDirection: 'LR',
  focusedStageIndex: null,
  compactMode: false,
  setName: noop,
  setNodes: noop,
  setEdges: noop,
  setStages: noop,
  setHosts: noop,
  setEngagement: noop,
  setIsEditMode: noop,
  setSelectedAttackPath: noop,
  setActiveView: noop,
  setLayoutDirection: noop,
  setFocusedStageIndex: noop,
  addNode: noop,
  removeNode: noop,
  updateNode: noop,
  addEdge: noop,
  removeEdge: noop,
  onNodesChange: noop,
  onEdgesChange: noop,
  isFetchingAttackPaths: false,
  isFetchingAttackPath: false,
  onEdit: noop,
  onCancel: noop,
  onSave: noop,
  onDelete: noop,
  onCreate: noop,
  onChange: noop,
  recenter: noop,
};

export interface ChatAttackGraphData {
  nodes: Array<{
    id: string;
    type: string;
    data: Record<string, unknown>;
    position?: { x: number; y: number };
  }>;
  /**
   * May be null at runtime: the backend marshals a nil Go slice as JSON
   * null and get_attack_graph round-trips stored files verbatim.
   */
  edges: Array<{ id: string; source: string; target: string }> | null;
  stages?: unknown[];
  /** Layout flow direction; absent on legacy graphs → treated as 'LR'. */
  direction?: 'LR' | 'TB';
}

interface ChatAttackGraphProps {
  name: string;
  graph: ChatAttackGraphData;
  /**
   * True when this graph was already saved server-side by the
   * save_attack_graph agent tool. Seeds the "saved" confirmation chip and
   * hides the in-chat Save button (the file already exists — re-saving from
   * here would be a confusing duplicate write). get_attack_graph results omit
   * this (read-only data the user may choose to save), keeping the Save button.
   */
  savedServerSide?: boolean;
}

function AttackGraphInner({
  name: defaultName,
  graph,
  savedServerSide = false,
}: ChatAttackGraphProps) {
  const { mutateAsync: uploadFile } = useUploadFile();
  const [saveName, setSaveName] = useState(defaultName);
  const [saved, setSaved] = useState(savedServerSide);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  const { data: existingFiles } = useMy({
    resource: 'file',
    query: `#${getAttackPathIdFromName('')}`,
  });

  // Resolve the typed name against existing files using the SAME precedence as
  // the backend resolveAttackPathName (attack_graph.go): exact match first, then
  // case-insensitive. We compare the prefix-stripped display names. When a match
  // is found we treat it as an overwrite AND save to that file's actual stored
  // name — never the typed casing — so the in-chat Save path (which writes via
  // PUT /file directly, bypassing the agent's case-insensitive save_attack_graph
  // tool) cannot create a case-variant duplicate (e.g. typing "a1b2c" when
  // "attack-path-A1B2C" already exists must overwrite A1B2C, not spawn a sibling).
  const matchedExistingName = useMemo(() => {
    const stripped = existingFiles.map(f => getAttackPathNameFromId(f.name));
    const exact = stripped.find(n => n === saveName);
    if (exact !== undefined) {
      return exact;
    }
    return stripped.find(n => n.toLowerCase() === saveName.toLowerCase());
  }, [existingFiles, saveName]);

  const willOverwrite = matchedExistingName !== undefined;

  // Single coercion point for null-edges graphs — every consumer below reads
  // safeEdges, never graph.edges.
  const safeEdges = graph.edges ?? EMPTY_EDGES;

  // Auto-layout nodes that are all stacked at (0,0) or missing positions
  const layoutNodes = useMemo(() => {
    if (graph.nodes.length === 0) return graph.nodes;
    const needsLayout = graph.nodes.every(
      n => !n.position || (n.position.x === 0 && n.position.y === 0)
    );
    if (!needsLayout) return graph.nodes;
    return computeLayout(graph.nodes as CustomNode[], safeEdges as Edge[], {
      direction: graph.direction ?? 'LR',
    });
  }, [graph.nodes, safeEdges, graph.direction]);

  const doSave = useCallback(async () => {
    try {
      await uploadFile({
        name: getAttackPathIdFromName(matchedExistingName ?? saveName),
        content: JSON.stringify({
          ...graph,
          nodes: layoutNodes,
          // safeEdges coalesces legacy `edges: null` graphs to [], so
          // re-saving heals the stored file instead of perpetuating the
          // malformed shape.
          edges: safeEdges,
        }),
      });
      // The Attack Paths page lists files via
      // useMy({ resource: 'file', query: '#attack-path-' }). useUploadFile's
      // generic invalidation is keyed off useMy({ resource: 'file' }) and, under
      // tenant impersonation, its `friend` segment collides positionally with
      // the list query's '#attack-path-' composite key, so the predicate misses
      // the list and the page shows stale data until a hard refresh. Explicitly
      // invalidate the file-list prefix (fuzzy match, impersonation-agnostic) so
      // navigating to Attack Paths refetches and shows the newly-saved graph.
      await queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('file'),
      });
      setSaved(true);
      setConfirmOverwrite(false);
      toast.success('Saved to Attack Paths');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save attack path');
    }
  }, [
    uploadFile,
    saveName,
    matchedExistingName,
    graph,
    layoutNodes,
    safeEdges,
  ]);

  const handleSave = useCallback(() => {
    if (!saveName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    if (willOverwrite && !confirmOverwrite) {
      setConfirmOverwrite(true);
      return;
    }
    void doSave();
  }, [saveName, willOverwrite, confirmOverwrite, doSave]);

  const coloredEdges: Edge[] = safeEdges.map(edge => {
    const sourceNode = graph.nodes.find(n => n.id === edge.source);
    const targetNode = graph.nodes.find(n => n.id === edge.target);
    const defaultHex = { light: '#6B7280', dark: '#9CA3AF' };

    const techNode =
      sourceNode?.type === 'technique'
        ? sourceNode
        : targetNode?.type === 'technique'
          ? targetNode
          : null;

    if (techNode) {
      const status = (techNode.data.status as string) || '';
      const color = techniqueColors[status];
      return {
        ...edge,
        type: 'bezier',
        className: color?.line,
        markerEnd: `arrow-${status}`,
        data: {
          strokeHex: color?.lineHex || defaultHex,
          markerHex: color?.markerHex || defaultHex,
          status,
        },
      } as Edge;
    }

    return {
      ...edge,
      type: 'bezier',
      markerEnd: 'arrow-',
      data: { strokeHex: defaultHex, markerHex: defaultHex, status: '' },
    } as Edge;
  });

  return (
    <div className="my-2">
      <div className="border-chariot-stroke overflow-hidden rounded-lg border">
        <div className="relative h-[350px]">
          <AttackContext.Provider
            value={{
              ...readOnlyContext,
              nodes: layoutNodes as never[],
              edges: coloredEdges,
              layoutDirection: graph.direction ?? 'LR',
            }}
          >
            <ReactFlow
              nodes={layoutNodes as never[]}
              edges={coloredEdges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultEdgeOptions={{ type: 'bezier' }}
              fitView
              fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
              panOnScroll
              panOnScrollMode={PanOnScrollMode.Vertical}
              preventScrolling={false}
              nodesConnectable={false}
              nodesDraggable={false}
              elementsSelectable={false}
              maxZoom={1.5}
              minZoom={0.3}
              className="size-full bg-transparent"
            >
              {/* Arrow markers */}
              <div className="absolute top-0 left-0">
                {Object.entries(markerColors).map(([key, colors]) => (
                  <svg
                    key={key}
                    style={{ position: 'absolute', top: 0, left: 0 }}
                  >
                    <defs>
                      <marker
                        id={`arrow-${key}`}
                        viewBox="0 0 10 10"
                        markerWidth={12}
                        markerHeight={8}
                        refX={8}
                        refY={5}
                        orient="auto"
                        markerUnits="strokeWidth"
                        className={colors.marker}
                      >
                        <path
                          d="M 0 0 L 10 5 L 0 10 z"
                          className="fill-current"
                        />
                      </marker>
                    </defs>
                  </svg>
                ))}
              </div>
              <Background color="rgb(var(--chariot-stroke) / 0.3)" size={4} />
            </ReactFlow>
          </AttackContext.Provider>
        </div>

        {/* Action bar */}
        <div className="border-chariot-stroke border-t px-3 py-2">
          {saved ? (
            <div className="flex items-center justify-between">
              <span className="text-chariot-text-tertiary text-xs">
                {graph.nodes.length} nodes · {safeEdges.length} edges
              </span>
              <span className="text-success flex items-center gap-1 text-xs">
                <span>✓</span>{' '}
                {saveName ? <>Saved as &quot;{saveName}&quot;</> : <>Saved</>}
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={saveName}
                  onChange={e => {
                    setSaveName(e.target.value);
                    setConfirmOverwrite(false);
                  }}
                  placeholder="Attack path name"
                  className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary min-w-0 flex-1 rounded border px-2 py-1 text-xs"
                />
                <button
                  onClick={handleSave}
                  className="text-chariot-brand-primary hover:bg-chariot-brand-primary/10 flex flex-shrink-0 items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors hover:cursor-pointer"
                >
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  {confirmOverwrite
                    ? 'Confirm Overwrite'
                    : 'Save to Attack Paths'}
                </button>
              </div>
              {confirmOverwrite && (
                <div className="flex items-center gap-2 text-xs text-yellow-500">
                  <span>
                    An attack path named &quot;{saveName}&quot; already exists.
                    Click again to overwrite.
                  </span>
                  <button
                    onClick={() => setConfirmOverwrite(false)}
                    className="text-chariot-text-tertiary hover:text-chariot-text-secondary underline"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ChatAttackGraph: React.FC<ChatAttackGraphProps> = props => (
  <ReactFlowProvider>
    <AttackGraphInner {...props} />
  </ReactFlowProvider>
);

export default ChatAttackGraph;
